/**
 * Visitor tracking helper.
 * Captures marketing & session attribution data on first page load and persists
 * UTMs in sessionStorage so they survive in-app navigation. Any form that submits
 * a lead can call `getTrackingPayload()` to get a clean object to merge into the
 * `/api/leads` payload.
 *
 * Captured fields (all optional, dropped if empty):
 *   - utmSource / utmMedium / utmCampaign / utmTerm / utmContent
 *   - gclid (Google Ads click id) / fbclid (Meta click id)
 *   - referrer (document.referrer)
 *   - landingPath  (e.g. "/zchuta")
 *   - landingUrl   (full first-page URL with query string)
 *   - userAgent / language / screenSize / viewportSize / timezone
 *   - firstVisitAt (ISO timestamp of first capture in this session)
 */

const STORAGE_KEY = 'gb_visitor_tracking_v1';

const UTM_KEYS = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_term', 'utmTerm'],
  ['utm_content', 'utmContent'],
  ['gclid', 'gclid'],
  ['fbclid', 'fbclid'],
];

// Centralised soft-warn helper — keeps catch blocks observable without
// breaking the page if browser storage / APIs are restricted.
const warn = (where, err) => {
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(`[tracking] ${where} failed:`, err);
  }
};

const readSession = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    warn('readSession', err);
    return null;
  }
};

const writeSession = (data) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    warn('writeSession', err);
  }
};

/**
 * Capture tracking data. Idempotent: subsequent calls return the same first-touch
 * snapshot, so UTM parameters are preserved even when the user lands on /zchuta?utm_*
 * and then internally navigates around.
 */
export const captureTracking = () => {
  // Guard for SSR / non-browser
  if (typeof window === 'undefined') return {};

  const existing = readSession();
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const data = {};

  // UTM + click ids from URL
  UTM_KEYS.forEach(([qsKey, dataKey]) => {
    const v = params.get(qsKey);
    if (v) data[dataKey] = v.slice(0, 200);
  });

  // Referrer
  try {
    if (document.referrer) data.referrer = document.referrer.slice(0, 500);
  } catch (err) {
    warn('referrer', err);
  }

  // Landing URL + path
  data.landingPath = window.location.pathname.slice(0, 200);
  data.landingUrl = window.location.href.slice(0, 500);

  // Device / locale
  if (navigator.userAgent) data.userAgent = navigator.userAgent.slice(0, 500);
  if (navigator.language) data.language = navigator.language.slice(0, 20);

  try {
    if (window.screen) {
      data.screenSize = `${window.screen.width}x${window.screen.height}`;
    }
  } catch (err) {
    warn('screenSize', err);
  }
  try {
    data.viewportSize = `${window.innerWidth}x${window.innerHeight}`;
  } catch (err) {
    warn('viewportSize', err);
  }
  try {
    data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (err) {
    warn('timezone', err);
  }

  data.firstVisitAt = new Date().toISOString();

  writeSession(data);
  return data;
};

/**
 * Get tracking payload for inclusion in a lead-submission body.
 * Returns ONLY non-empty fields so the backend stays clean.
 */
export const getTrackingPayload = () => {
  const data = captureTracking();
  const out = {};
  Object.keys(data || {}).forEach((k) => {
    if (data[k] !== undefined && data[k] !== null && data[k] !== '') {
      out[k] = data[k];
    }
  });
  return out;
};

/**
 * Friendly Hebrew labels for the admin-side "פרטי גלישה" detail panel.
 */
export const TRACKING_LABELS = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmTerm: 'utm_term',
  utmContent: 'utm_content',
  gclid: 'Google Click ID',
  fbclid: 'Meta Click ID',
  referrer: 'הפניה (Referrer)',
  landingPath: 'דף נחיתה',
  landingUrl: 'URL מלא',
  userAgent: 'User Agent',
  language: 'שפה',
  screenSize: 'רזולוציית מסך',
  viewportSize: 'גודל חלון',
  timezone: 'אזור זמן',
  firstVisitAt: 'ביקור ראשון (UTC)',
};

export const TRACKING_KEYS = Object.keys(TRACKING_LABELS);
